import React from 'react';
import type { Issue, RootCauseAnalysis } from '../../types/issue';
import { isTransitionAllowed, MONITORING_MODE_LABELS, VALIDATION_MODE_LABELS } from '../../types/issue';
import type { ActionStatus, EnterpriseAction } from '../../services/actionService';
import type { GembaNoteRecord, IssueMeasureRecord, StratumRecord } from '../../services/issueMaspService';
import type { CauseCheckView } from '../../utils/causeWork';
import type { CauseObservationContext } from './CauseAnalysisView';
import type { IssueEvidenceRecord } from '../../services/issueEvidenceService';
import { stageRelation } from '../../utils/problemStageNav';
import { QUICK_RESOLUTION_STAGES, RESOLUTION_STAGES, stageHasRecord } from '../../utils/issueProgress';
import { presentControls, presentLosses } from '../../utils/issueMasp';
import { hasInvestigationFact, isCauseConfirmed } from '../../utils/problemCause';
import { presentStageLabel } from '../../utils/structuredStageLabel';
import { efficacyAttempts } from '../../utils/efficacyCheck';
import { ProblemActionsBlock } from './ProblemActionsBlock';
import { CauseWorkspace } from './CauseWorkspace';
import { CauseConsultationCard } from './CauseConsultationCard';
import { IdentificationWorkspace } from './IdentificationWorkspace';
import { ObservationWorkspace } from './ObservationWorkspace';
import { ActionPlanWorkspace } from './ActionPlanWorkspace';
import { ExecutionWorkspace } from './ExecutionWorkspace';
import { VerificationWorkspace } from './VerificationWorkspace';
import { StandardizationWorkspace } from './StandardizationWorkspace';
import { ConclusionWorkspace } from './ConclusionWorkspace';
import type { ActionCountermeasureRecord } from '../../services/actionCountermeasureService';
import type { IssueMaspEventRecord, StandardizationEventRecord } from '../../services/issueMaspService';
import type { StandardizationKind } from '../../utils/standardization';

export interface MaspEditors {
  measures: IssueMeasureRecord[];
  strata: StratumRecord[];
  gemba: GembaNoteRecord[];
  evidence: IssueEvidenceRecord[];
  onAddLoss: (input: { name: string; unit: string; value: number }) => Promise<void>;
  onSaveControl: (input: {
    name: string;
    unit: string;
    direction: 'higher_is_better' | 'lower_is_better' | 'target_is_best';
    baseline: number;
    target: number;
  }) => Promise<void>;
  onRemoveMeasure: (id: string) => Promise<void>;
  onAddStratum: (dimension: string) => Promise<void>;
  onRemoveStratum: (id: string) => Promise<void>;
  onAddSlice: (input: {
    stratumId: string;
    label: string;
    occurrenceCount: number | null;
    amount: number | null;
    measureId: string | null;
  }) => Promise<void>;
  onRemoveSlice: (id: string) => Promise<void>;
  onAddGemba: (input: { fact: string; place: string; observedOn: string }) => Promise<void>;
  onRemoveGemba: (id: string) => Promise<void>;
  loadError?: boolean;
  onAddEvidence: (file: File, caption: string) => Promise<void>;
  onDeleteEvidence: (record: IssueEvidenceRecord) => Promise<void>;
  onBeginCause: () => void;
  onBeginObservation: () => void;
  pending?: boolean;
  causeEvidence?: IssueEvidenceRecord[];
  causeCheck?: CauseCheckView | null;
  onAddHypothesisEvidence?: (analysis: RootCauseAnalysis, hypothesisId: string, file: File, caption: string) => Promise<void>;
  onDeleteHypothesisEvidence?: (record: IssueEvidenceRecord) => Promise<void>;
  onSaveCauseCheck?: (check: CauseCheckView) => Promise<void>;
}

interface ProblemStagePaneProps {
  issue: Issue;
  actions: EnterpriseAction[];
  assigneeNames: Record<string, string>;
  stageKey: string;
  stageLabel: string;
  quick: boolean;
  efficacyPanel?: React.ReactNode;
  onOpenCause: () => void;
  onOpenActions: () => void;
  onOpenAction: (action: EnterpriseAction) => void;
  onCreateAction: () => void;
  onEditCountermeasure?: (action: EnterpriseAction) => void;
  onCancelCountermeasure?: (action: EnterpriseAction) => void;
  countermeasures?: ActionCountermeasureRecord[];
  executionEvents?: IssueMaspEventRecord[];
  executionEvidence?: IssueEvidenceRecord[];
  onChangeActionStatus?: (action: EnterpriseAction, status: ActionStatus) => Promise<void>;
  onAddExecutionEvent?: (input: { kind: 'training' | 'deviation'; title: string; body: string; occurredOn: string }) => Promise<void>;
  onAddExecutionEvidence?: (file: File, caption: string) => Promise<void>;
  onDeleteExecutionEvidence?: (record: IssueEvidenceRecord) => Promise<void>;
  validationEvidence?: IssueEvidenceRecord[];
  actorId?: string;
  actorRole?: string | null;
  onAddAfterPoint?: (measureId: string, value: number, occurredOn: string) => Promise<void>;
  onConfirmEfficacy?: (note: string) => Promise<void>;
  onRejectEfficacy?: (note: string) => Promise<void>;
  onAddValidationEvidence?: (file: File, caption: string) => Promise<void>;
  onDeleteValidationEvidence?: (record: IssueEvidenceRecord) => Promise<void>;
  standardizationEvents?: StandardizationEventRecord[];
  onAddStandardizationEvent?: (input: {
    kind: StandardizationKind;
    title: string;
    body: string;
    audience: string;
    result: string;
    occurredOn: string;
  }) => Promise<void>;
  onSelectStage?: (key: string) => void;
  onSaveAnalysis?: (analysis: RootCauseAnalysis) => void;
  savingAnalysis?: boolean;
  masp?: MaspEditors;
  stageFocus?: string | null;
  actionsState?: 'loading' | 'error';
  eventsState?: 'error';
  executionEventsState?: 'error';
}

export const ProblemStagePane: React.FC<ProblemStagePaneProps> = ({
  issue,
  actions,
  assigneeNames,
  stageKey,
  stageLabel,
  quick,
  efficacyPanel,
  onOpenCause,
  onOpenActions,
  onOpenAction,
  onCreateAction,
  onEditCountermeasure,
  onCancelCountermeasure,
  countermeasures = [],
  executionEvents = [],
  executionEvidence = [],
  onChangeActionStatus,
  onAddExecutionEvent,
  onAddExecutionEvidence,
  onDeleteExecutionEvidence,
  validationEvidence = [],
  actorId,
  actorRole,
  onAddAfterPoint,
  onConfirmEfficacy,
  onRejectEfficacy,
  onAddValidationEvidence,
  onDeleteValidationEvidence,
  standardizationEvents = [],
  onAddStandardizationEvent,
  onSelectStage,
  onSaveAnalysis,
  savingAnalysis,
  masp,
  stageFocus,
  actionsState,
  eventsState,
  executionEventsState,
}) => {
  const relation = stageRelation(issue, stageKey);
  const futureWithoutRecord = relation === 'future' && !stageHasRecord(issue, actions, stageKey);
  const futureCause = futureWithoutRecord && !quick && stageKey === 'root';

  if (!quick && stageKey === 'plan') {
    return (
      <section id="stage-work" aria-labelledby="stage-work-title">
        <h2 id="stage-work-title" className="text-[13px] font-semibold text-slate-900 dark:text-slate-50">
          {stageLabel}
        </h2>
        <div className="mt-1">
          <ActionPlanWorkspace
            issue={issue}
            actions={actions}
            assigneeNames={assigneeNames}
            extensions={countermeasures}
            causeCheck={masp?.causeCheck ?? null}
            onCreate={onCreateAction}
            onEdit={onEditCountermeasure ?? onOpenAction}
            onCancel={onCancelCountermeasure ?? (() => undefined)}
            onOpenAnalysis={() => onSelectStage?.('root')}
            actionsState={actionsState}
          />
        </div>
      </section>
    );
  }

  if (!quick && stageKey === 'exec') {
    return (
      <section id="stage-work" aria-labelledby="stage-work-title">
        <h2 id="stage-work-title" className="text-[13px] font-semibold text-slate-900 dark:text-slate-50">
          {stageLabel}
        </h2>
        <div className="mt-1">
          <ExecutionWorkspace
            actions={actions.filter((action) => action.problem_id === issue.id)}
            assigneeNames={assigneeNames}
            extensions={countermeasures}
            events={executionEvents}
            evidence={executionEvidence}
            closed={issue.status === 'closed'}
            onOpenPlan={() => onSelectStage?.('plan')}
            onChangeStatus={onChangeActionStatus ?? (async () => undefined)}
            onAddEvent={onAddExecutionEvent ?? (async () => undefined)}
            onAddEvidence={onAddExecutionEvidence ?? (async () => undefined)}
            onDeleteEvidence={onDeleteExecutionEvidence ?? (async () => undefined)}
            actionsState={actionsState}
            eventsState={executionEventsState}
          />
        </div>
      </section>
    );
  }

  if (!quick && stageKey === 'valid') {
    return (
      <section id="stage-work" aria-labelledby="stage-work-title">
        <h2 id="stage-work-title" className="text-[13px] font-semibold text-slate-900 dark:text-slate-50">
          {stageLabel}
        </h2>
        <div className="mt-1">
          <VerificationWorkspace
            issue={issue}
            actions={actions.filter((action) => action.problem_id === issue.id)}
            measures={masp?.measures ?? []}
            evidence={validationEvidence}
            actorId={actorId}
            actorRole={actorRole}
            closed={issue.status === 'closed'}
            onAddAfter={onAddAfterPoint ?? (async () => undefined)}
            onConfirmEfficacy={onConfirmEfficacy ?? (async () => undefined)}
            onRejectEfficacy={onRejectEfficacy ?? (async () => undefined)}
            onAddEvidence={onAddValidationEvidence ?? (async () => undefined)}
            onDeleteEvidence={onDeleteValidationEvidence ?? (async () => undefined)}
            pending={masp?.pending}
            loadError={masp?.loadError}
          />
        </div>
      </section>
    );
  }

  if (!quick && stageKey === 'monitor') {
    return (
      <section id="stage-work" aria-labelledby="stage-work-title">
        <h2 id="stage-work-title" className="text-[13px] font-semibold text-slate-900 dark:text-slate-50">
          {stageLabel}
        </h2>
        <div className="mt-1">
          <StandardizationWorkspace
            issue={issue}
            actions={actions.filter((action) => action.problem_id === issue.id)}
            events={standardizationEvents}
            closed={issue.status === 'closed'}
            onAddEvent={onAddStandardizationEvent ?? (async () => undefined)}
            eventsState={eventsState}
          />
        </div>
      </section>
    );
  }

  if (!quick && stageKey === 'close') {
    return (
      <section id="stage-work" aria-labelledby="stage-work-title">
        <h2 id="stage-work-title" className="text-[13px] font-semibold text-slate-900 dark:text-slate-50">
          {stageLabel}
        </h2>
        <div className="mt-1">
          <ConclusionWorkspace
            issue={issue}
            actions={actions.filter((action) => action.problem_id === issue.id)}
            measures={masp?.measures ?? []}
            events={standardizationEvents}
            pending={masp?.pending}
            loadError={masp?.loadError}
            actionsState={actionsState}
          />
        </div>
      </section>
    );
  }

  return (
    <section id="stage-work" aria-labelledby="stage-work-title">
      <h2 id="stage-work-title" className="text-[13px] font-semibold text-slate-900 dark:text-slate-50">
        {stageLabel}
      </h2>
      <div className="mt-1">
        {futureCause ? (
          <CauseConsultationCard
            issue={issue}
            counts={{ strata: masp?.strata.length ?? 0, gemba: masp?.gemba.length ?? 0 }}
            onDescribe={() => onSelectStage?.('analysis')}
            onBeginObservation={masp?.onBeginObservation}
            onBeginCause={masp?.onBeginCause}
          />
        ) : futureWithoutRecord ? (
          <StageEmptyState stageKey={stageKey} quick={quick} onSelectStage={onSelectStage} />
        ) : (
          <StageBody
            issue={issue}
            actions={actions}
            assigneeNames={assigneeNames}
            stageKey={stageKey}
            quick={quick}
            efficacyPanel={efficacyPanel}
            onOpenCause={onOpenCause}
            onOpenActions={onOpenActions}
            onOpenAction={onOpenAction}
            onCreateAction={onCreateAction}
            onSelectStage={onSelectStage}
            onSaveAnalysis={onSaveAnalysis}
            savingAnalysis={savingAnalysis}
            masp={masp}
            stageFocus={stageFocus}
            closed={issue.status === 'closed'}
            formReadOnly={relation === 'future'}
            actionsState={actionsState}
          />
        )}
      </div>
    </section>
  );
};

function StageBody({
  issue,
  actions,
  assigneeNames,
  stageKey,
  quick,
  efficacyPanel,
  onOpenCause,
  onOpenActions,
  onOpenAction,
  onCreateAction,
  onSelectStage,
  onSaveAnalysis,
  savingAnalysis,
  masp,
  stageFocus,
  closed,
  formReadOnly,
  actionsState,
}: Omit<ProblemStagePaneProps, 'stageLabel'> & { closed?: boolean; formReadOnly?: boolean }) {
  const analysis = issue.root_cause_analysis;

  if (stageKey === 'ident') {
    const responsibleId = issue.assigned_to || issue.owner_id;
    const responsible = responsibleId ? (assigneeNames[responsibleId] || 'Responsável atribuído') : '';
    const measures = masp?.measures ?? [];
    const canEditMasp = !quick && !closed && !formReadOnly && Boolean(masp);
    return (
      <IdentificationWorkspace
        issue={issue}
        assigneeName={responsible || 'Sem responsável'}
        losses={presentLosses(measures)}
        controls={presentControls(measures)}
        editable={canEditMasp}
        pending={masp?.pending}
        loadError={masp?.loadError}
        onAddLoss={masp?.onAddLoss}
        onSaveControl={masp?.onSaveControl}
        onRemoveMeasure={masp?.onRemoveMeasure}
        onDescribePhenomenon={() => onSelectStage?.('analysis')}
        focusToken={stageFocus}
      />
    );
  }

  if (stageKey === 'analysis' && !quick) {
    const canEdit = !closed && !formReadOnly;
    const canEditMasp = canEdit && Boolean(masp);
    return (
      <ObservationWorkspace
        statement={analysis?.problem_statement ?? ''}
        analysis={analysis ?? null}
        editable={canEdit && Boolean(onSaveAnalysis || masp)}
        saving={savingAnalysis}
        measures={masp?.measures ?? []}
        strata={masp?.strata ?? []}
        gemba={masp?.gemba ?? []}
        evidence={masp?.evidence ?? []}
        names={assigneeNames}
        pending={masp?.pending}
        loadError={masp?.loadError}
        onSaveStatement={onSaveAnalysis ? (text) => onSaveAnalysis(withStatement(analysis, text)) : undefined}
        onAddStratum={canEditMasp ? masp?.onAddStratum : undefined}
        onRemoveStratum={canEditMasp ? masp?.onRemoveStratum : undefined}
        onAddSlice={canEditMasp ? masp?.onAddSlice : undefined}
        onRemoveSlice={canEditMasp ? masp?.onRemoveSlice : undefined}
        onAddGemba={canEditMasp ? masp?.onAddGemba : undefined}
        onRemoveGemba={canEditMasp ? masp?.onRemoveGemba : undefined}
        onAddEvidence={canEditMasp ? masp?.onAddEvidence : undefined}
        onDeleteEvidence={canEditMasp ? masp?.onDeleteEvidence : undefined}
        onOpenCause={() => onSelectStage?.('root')}
        focusToken={stageFocus}
        onWorkCause={() => {
          if (masp && hasInvestigationFact(issue) && isTransitionAllowed(issue.mode, issue.status, 'root_cause')) {
            masp.onBeginCause();
            return;
          }
          onSelectStage?.('root');
        }}
      />
    );
  }

  if (stageKey === 'analysis' || stageKey === 'root') {
    const confirmed = isCauseConfirmed(issue);
    const observation: CauseObservationContext | undefined = masp ? {
      strata: masp.strata.slice(0, 2).map((item) => ({
        dimension: item.dimension,
        highlight: item.slices.slice(0, 2).map((slice) => (
          slice.occurrenceCount != null ? `${slice.label} ${slice.occurrenceCount}` : slice.label
        )).join(' · '),
      })),
      gemba: masp.gemba.slice(0, 2).map((item) => ({ fact: item.fact })),
      evidenceCount: masp.evidence.length,
    } : undefined;
    return (
      <div className="space-y-4">
        <CauseWorkspace
          value={analysis ?? null}
          focus={stageKey === 'analysis' ? 'investigation' : 'cause'}
          readOnly={closed || formReadOnly || !onSaveAnalysis}
          saving={savingAnalysis}
          onSave={onSaveAnalysis ?? (() => undefined)}
          onContinueToCause={stageKey === 'analysis' ? () => onSelectStage?.('root') : undefined}
          onReviewObservation={stageKey === 'root' ? () => onSelectStage?.('analysis') : undefined}
          observation={observation}
          evidence={masp?.causeEvidence}
          onAddEvidence={masp?.onAddHypothesisEvidence}
          onDeleteEvidence={masp?.onDeleteHypothesisEvidence}
          causeCheck={masp?.causeCheck}
          onSaveCauseCheck={masp?.onSaveCauseCheck}
          focusToken={stageFocus}
          onDefinePlan={!closed ? onCreateAction : undefined}
        />
        {stageKey === 'root' && confirmed ? (
          <div className="max-w-[720px] space-y-1 border-t border-slate-200 pt-3">
            <p className="text-[12px] font-medium text-slate-800">Tratamento</p>
            {actions.length === 0 ? (
              <p className="text-[13px] text-slate-600">{actionCollectionMessage(actionsState, 'Nenhuma contramedida definida.')}</p>
            ) : (
              <ul className="text-[13px] text-slate-700">
                {actions.map((action) => <li key={action.id}>• {action.title}</li>)}
              </ul>
            )}
            {!closed ? (
              <button type="button" onClick={onCreateAction} className="text-[12px] font-medium text-slate-700 hover:underline">
                + Criar contramedida
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  if (stageKey === 'plan' || stageKey === 'exec') {
    const confirmed = isCauseConfirmed(issue);
    const withOwner = actions.filter((action) => action.assigned_to).length;
    const withDue = actions.filter((action) => action.due_date).length;
    const doing = actions.filter((action) => action.status === 'in_progress').length;
    const done = actions.filter((action) => action.status === 'completed').length;
    const planKnown = [
      confirmed ? `Causa: ${issue.root_cause_analysis?.root_cause_description}` : 'Causa ainda não consolidada',
      actions.length > 0 ? `${actions.length} contramedidas` : '',
      withOwner > 0 ? `${withOwner} com responsável` : '',
      withDue > 0 ? `${withDue} com prazo` : '',
    ].filter(Boolean).join(' · ');
    return (
      <div className="max-w-[960px] space-y-3">
        <Orientation
          known={stageKey === 'plan'
            ? planKnown
            : `${done} concluídas · ${doing} em execução · ${actions.length} no plano`}
          missing={stageKey === 'plan'
            ? (actions.length === 0 ? 'Nenhuma contramedida registrada.' : 'Complete responsável e prazo de cada contramedida.')
            : (done === actions.length && actions.length > 0 ? 'As contramedidas desta etapa estão concluídas.' : 'Há contramedidas por concluir.')}
          next={stageKey === 'plan'
            ? (actions.length === 0 ? 'Registrar a contramedida. A causa não cria a ação.' : 'Executar as contramedidas definidas.')
            : (actions.length > 0 && done === actions.length ? 'Verificar se o fenômeno parou.' : 'Concluir as contramedidas.')}
          evidence=""
        />
        {confirmed ? (
          <div className="space-y-1 text-[13px] text-slate-700">
            <p className="font-medium text-slate-900">Causa confirmada</p>
            <p>{issue.root_cause_analysis?.root_cause_description}</p>
            {issue.root_cause_analysis?.cause_evidence ? <p className="text-slate-500">Evidência: {issue.root_cause_analysis.cause_evidence}</p> : null}
          </div>
        ) : null}
        {actions.length === 0 ? (
          <div>
            <p className="text-[13px] text-slate-600">
              {actionCollectionMessage(actionsState, quick ? 'A correção ainda não tem contramedida.' : 'Nenhuma contramedida definida.')}
            </p>
            {issue.status !== 'closed' ? (
              <button type="button" onClick={onCreateAction} className="mt-2 rounded border border-slate-300 px-2 py-1 text-[12px] font-medium text-slate-700">
                Criar contramedida
              </button>
            ) : null}
          </div>
        ) : (
          <ProblemActionsBlock
            actions={actions}
            assigneeNames={assigneeNames}
            variant={quick ? 'quick' : 'structured'}
            readOnly
            onRowClick={onOpenAction}
            onCreateAction={onCreateAction}
          />
        )}
      </div>
    );
  }

  if (stageKey === 'valid') {
    const attempts = efficacyAttempts(issue.metadata);
    const result = issue.validation_config?.result;
    const known = [
      issue.validation_config?.mode ? VALIDATION_MODE_LABELS[issue.validation_config.mode] : '',
      result === 'passed' ? 'Eficácia aceita' : result === 'failed' ? 'Eficácia rejeitada' : '',
      attempts.length > 0 ? `${attempts.length} verificação(ões) de eficácia` : '',
    ].filter(Boolean).join(' · ');
    return (
      <div className="max-w-[920px] space-y-2">
        <Orientation
          known={known || 'Ainda sem verificação registrada.'}
          missing="A verificação usa a eficácia já registrada."
          next="Verificar se o problema continua ocorrendo."
          evidence=""
        />
        {efficacyPanel}
      </div>
    );
  }

  if (stageKey === 'monitor') {
    const config = issue.monitoring_config;
    const known = config
      ? [
        MONITORING_MODE_LABELS[config.mode],
        config.checks_count != null ? `${config.checks_count} verificação(ões)` : '',
        config.last_check_at ? `Última em ${new Date(config.last_check_at).toLocaleDateString('pt-BR')}` : '',
        issue.metadata?.recurrence_detected ? 'Recorrência sinalizada' : '',
      ].filter(Boolean).join(' · ')
      : 'Não há acompanhamento registrado.';
    return (
      <div className="max-w-[920px] space-y-2 text-[13px] text-slate-700 dark:text-slate-200">
        <Orientation
          known={known}
          missing={config ? 'Acompanhe a recorrência contra o padrão.' : 'O acompanhamento ainda não foi registrado.'}
          next={config ? 'Acompanhar recorrência contra o padrão.' : 'Registrar o acompanhamento.'}
          evidence=""
        />
        {!config ? <p className="font-medium text-slate-800 dark:text-slate-100">Não há acompanhamento registrado para este problema.</p> : null}
      </div>
    );
  }

  const completed = actions.filter((action) => action.status === 'completed');
  const closeKnown = [
    issue.status === 'closed' ? 'Problema encerrado' : 'Ainda aberto',
    completed.length > 0 ? `${completed.length} contramedidas concluídas` : '',
    issue.metadata?.closure_reason?.trim() ? issue.metadata.closure_reason.trim() : '',
    issue.resolution?.trim() ? issue.resolution.trim() : '',
  ].filter(Boolean).join(' · ');
  return (
    <div className="max-w-[920px] space-y-2 text-[13px] text-slate-700 dark:text-slate-200">
      <Orientation
        known={closeKnown}
        missing={issue.metadata?.closure_reason?.trim() ? 'O motivo do encerramento já está informado.' : 'O motivo do encerramento ainda não foi informado.'}
        next={issue.status === 'closed' ? 'Conclusão registrada.' : 'Encerrar somente quando a verificação permitir.'}
        evidence=""
      />
      <p className="font-medium text-slate-900 dark:text-slate-50">Problema encerrado</p>
      {issue.description ? <p>{issue.description}</p> : null}
      <p>
        {completed.length > 0
          ? `${completed.length} contramedida(s) concluída(s).`
          : issue.metadata?.closure_reason
            ? `Encerrado sem contramedida. ${issue.metadata.closure_reason}`
            : 'Encerrado sem contramedida registrada.'}
      </p>
      {efficacyAttempts(issue.metadata).length > 0 ? <div className="pt-2">{efficacyPanel}</div> : null}
    </div>
  );
}

const FUTURE_HINT: Record<string, { text: string; backKey: string }> = {
  analysis: { text: 'A identificação vem antes da observação.', backKey: 'ident' },
  root: { text: 'A observação do fenômeno vem antes da análise de causa.', backKey: 'analysis' },
  plan: { text: 'O tratamento começa depois da causa.', backKey: 'root' },
  exec: { text: 'A execução começa depois do plano.', backKey: 'plan' },
  valid: { text: 'A verificação começa depois da execução.', backKey: 'exec' },
  monitor: { text: 'A padronização começa depois da verificação.', backKey: 'valid' },
  close: { text: 'A conclusão só existe quando o problema for fechado.', backKey: 'monitor' },
};

function Orientation({ known, missing, next, evidence }: { known: string; missing: string; next: string; evidence: string }) {
  return (
    <div className="space-y-1 text-[12px] leading-snug text-slate-600">
      <p><span className="font-semibold text-slate-800">Sabemos. </span>{known}</p>
      <p><span className="font-semibold text-slate-800">Falta. </span>{missing}</p>
      <p><span className="font-semibold text-slate-800">Próxima ação. </span>{next}</p>
      {evidence ? <p className="text-slate-400">{evidence}</p> : null}
    </div>
  );
}

function withStatement(analysis: RootCauseAnalysis | null | undefined, statement: string): RootCauseAnalysis {
  return {
    problem_statement: statement,
    root_cause_description: analysis?.root_cause_description ?? '',
    root_cause_category: analysis?.root_cause_category ?? 'method',
    five_whys: analysis?.five_whys,
    ishikawa: analysis?.ishikawa,
    suggested_hypotheses: analysis?.suggested_hypotheses,
    cause_confirmed: analysis?.cause_confirmed,
    cause_evidence: analysis?.cause_evidence,
    analysis_method: analysis?.analysis_method,
  };
}

function actionCollectionMessage(state: 'loading' | 'error' | undefined, empty: string): string {
  if (state === 'loading') return 'Carregando contramedidas.';
  if (state === 'error') return 'Não foi possível carregar as contramedidas.';
  return empty;
}

function StageEmptyState({ stageKey, quick, onSelectStage }: { stageKey: string; quick: boolean; onSelectStage?: (key: string) => void }) {
  const hint = quick && stageKey === 'plan'
    ? { text: 'A correção começa depois da identificação.', backKey: 'ident' }
    : quick && stageKey === 'close'
      ? { text: 'O encerramento só existe quando o problema for fechado.', backKey: 'valid' }
      : FUTURE_HINT[stageKey];
  const mode = quick ? 'quick_correction' : 'structured_problem';
  const fallback = hint?.backKey
    ? (quick ? QUICK_RESOLUTION_STAGES : RESOLUTION_STAGES).find((stage) => stage.key === hint.backKey)?.label ?? hint.backKey
    : '';
  const backLabel = hint?.backKey ? `Voltar para ${presentStageLabel(mode, hint.backKey, fallback)}` : '';
  return (
    <div className="max-w-xl">
      <p className="text-[13px] text-slate-600">{hint?.text ?? 'Não há registro nesta etapa.'}</p>
      {hint?.backKey && onSelectStage ? (
        <button type="button" onClick={() => onSelectStage(hint.backKey)} className="mt-2 text-[12px] font-medium text-slate-700 hover:underline">
          {backLabel}
        </button>
      ) : null}
    </div>
  );
}
